/**
 * Minimal GLB (Binary glTF 2.0) parser.
 * Extracts first mesh primitive: positions, normals, UVs, indices, base-color texture.
 * Runs at init time only — allocations are acceptable here.
 */

const MAGIC = 0x46546C67;
const JSON_CHUNK = 0x4E4F534A;
const BIN_CHUNK = 0x004E4942;

const TYPE_COMP: Record<string, number> = {
  SCALAR: 1, VEC2: 2, VEC3: 3, VEC4: 4,
};
const COMP_BYTES: Record<number, number> = {
  5120: 1, 5121: 1, 5122: 2, 5123: 2, 5125: 4, 5126: 4,
};

interface GltfAccessor {
  bufferView: number;
  byteOffset?: number;
  componentType: number;
  count: number;
  type: string;
}

interface GltfBufferView {
  byteOffset?: number;
  byteLength: number;
  byteStride?: number;
}

interface GltfImage {
  bufferView?: number;
  mimeType?: string;
}

interface GltfJson {
  meshes?: { primitives: { attributes: Record<string, number>; indices?: number; material?: number }[] }[];
  accessors: GltfAccessor[];
  bufferViews: GltfBufferView[];
  materials?: { pbrMetallicRoughness?: { baseColorTexture?: { index: number } } }[];
  textures?: { source: number }[];
  images?: GltfImage[];
}

export interface TextureImage {
  bytes: Uint8Array;
  mimeType: string;
}

export interface ParsedMesh {
  positions: Float32Array;
  normals: Float32Array | null;
  uvs: Float32Array | null;
  indices: Uint16Array | Uint32Array | null;
  baseColorImage: TextureImage | null;
}

export async function loadGlb(url: string): Promise<ParsedMesh> {
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`GLB fetch failed: ${url} (${resp.status})`);
  return parseGlb(await resp.arrayBuffer());
}

export function parseGlb(buffer: ArrayBuffer): ParsedMesh {
  const dv = new DataView(buffer);

  if (dv.getUint32(0, true) !== MAGIC) throw new Error('Not a GLB file');
  if (dv.getUint32(4, true) !== 2) throw new Error('Unsupported glTF version');

  let off = 12;
  const jsonLen = dv.getUint32(off, true);
  if (dv.getUint32(off + 4, true) !== JSON_CHUNK) throw new Error('Expected JSON chunk');
  const json: GltfJson = JSON.parse(new TextDecoder().decode(new Uint8Array(buffer, off + 8, jsonLen)));
  off += 8 + jsonLen;

  if (dv.getUint32(off + 4, true) !== BIN_CHUNK) throw new Error('Expected BIN chunk');
  const binStart = off + 8;

  const mesh = json.meshes?.[0];
  if (!mesh) throw new Error('No meshes in GLB');
  const prim = mesh.primitives[0];

  const positions = readAccessor(json, buffer, binStart, prim.attributes.POSITION) as Float32Array;
  const normals = prim.attributes.NORMAL !== undefined
    ? readAccessor(json, buffer, binStart, prim.attributes.NORMAL) as Float32Array
    : null;
  const uvs = prim.attributes.TEXCOORD_0 !== undefined
    ? readAccessor(json, buffer, binStart, prim.attributes.TEXCOORD_0) as Float32Array
    : null;
  const indices = prim.indices !== undefined
    ? readAccessor(json, buffer, binStart, prim.indices) as Uint16Array | Uint32Array
    : null;

  const baseColorImage = extractBaseColor(json, buffer, binStart, prim.material);

  return { positions, normals, uvs, indices, baseColorImage };
}

function extractBaseColor(
  json: GltfJson, buf: ArrayBuffer, binStart: number, matIdx: number | undefined,
): TextureImage | null {
  if (matIdx === undefined) return null;
  const mat = json.materials?.[matIdx];
  const texInfo = mat?.pbrMetallicRoughness?.baseColorTexture;
  if (!texInfo) return null;
  const tex = json.textures?.[texInfo.index];
  if (!tex) return null;
  const img = json.images?.[tex.source];
  if (!img || img.bufferView === undefined) return null;
  const bv = json.bufferViews[img.bufferView];
  const imgOff = binStart + (bv.byteOffset ?? 0);
  return {
    bytes: new Uint8Array(buf, imgOff, bv.byteLength),
    mimeType: img.mimeType ?? 'image/png',
  };
}

function readAccessor(
  json: GltfJson, buf: ArrayBuffer, binStart: number, accIdx: number,
): Float32Array | Uint16Array | Uint32Array {
  const acc = json.accessors[accIdx];
  const bv = json.bufferViews[acc.bufferView];
  const compCount = TYPE_COMP[acc.type] ?? 1;
  const totalElems = acc.count * compCount;
  const compSize = COMP_BYTES[acc.componentType] ?? 4;
  const baseOff = binStart + (bv.byteOffset ?? 0) + (acc.byteOffset ?? 0);
  const stride = bv.byteStride ?? 0;
  const naturalStride = compSize * compCount;

  if (stride !== 0 && stride !== naturalStride) {
    return deinterleave(buf, baseOff, acc.count, compCount, compSize, stride, acc.componentType);
  }

  return viewTyped(buf, baseOff, totalElems, acc.componentType);
}

function viewTyped(
  buf: ArrayBuffer, off: number, count: number, compType: number,
): Float32Array | Uint16Array | Uint32Array {
  switch (compType) {
    case 5126: return safeView(Float32Array, buf, off, count, 4);
    case 5123: return safeView(Uint16Array, buf, off, count, 2);
    case 5125: return safeView(Uint32Array, buf, off, count, 4);
    default: throw new Error(`Unsupported componentType: ${compType}`);
  }
}

function safeView<T extends Float32Array | Uint16Array | Uint32Array>(
  Ctor: { new(buf: ArrayBuffer, off: number, len: number): T; new(len: number): T; BYTES_PER_ELEMENT: number },
  buf: ArrayBuffer, off: number, count: number, align: number,
): T {
  if (off % align === 0) return new Ctor(buf, off, count);
  const dst = new Ctor(count);
  new Uint8Array(dst.buffer).set(new Uint8Array(buf, off, count * align));
  return dst;
}

function deinterleave(
  buf: ArrayBuffer, baseOff: number, count: number,
  compCount: number, compSize: number, stride: number, compType: number,
): Float32Array | Uint16Array | Uint32Array {
  const dv = new DataView(buf);
  const total = count * compCount;
  const read = compType === 5126 ? (o: number) => dv.getFloat32(o, true)
    : compType === 5125 ? (o: number) => dv.getUint32(o, true)
    : (o: number) => dv.getUint16(o, true);

  const out = compType === 5126 ? new Float32Array(total)
    : compType === 5125 ? new Uint32Array(total)
    : new Uint16Array(total);

  for (let i = 0; i < count; i++) {
    const src = baseOff + i * stride;
    for (let c = 0; c < compCount; c++) {
      out[i * compCount + c] = read(src + c * compSize);
    }
  }
  return out;
}
